import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import {
  AuthProvider,
  useAuth,
  isOperator,
  isPartner,
  homePathFor,
} from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { OperatorLayout, MemberLayout, PartnerLayout } from './components/layout';

// Public
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import InviteAccept from './pages/InviteAccept';
import AccountSetup from './pages/AccountSetup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PublicSurvey from './pages/PublicSurvey';
import Onboarding from './pages/Onboarding';

// Operator console
import WorkspacesList from './pages/operator/WorkspacesList';
import WorkspaceNew from './pages/operator/WorkspaceNew';
import WorkspaceDetail from './pages/operator/WorkspaceDetail';
import OperatorSurvey from './pages/operator/OperatorSurvey';
import OperatorReport from './pages/operator/OperatorReport';
import SurveyTemplates from './pages/operator/SurveyTemplates';
import AgentSettings from './pages/operator/AgentSettings';
import OAuthConsent from './pages/operator/OAuthConsent';
import OnboardingList from './pages/operator/OnboardingList';
import OnboardingSession from './pages/operator/OnboardingSession';
import Admin from './pages/Admin';
import AdminFeedback from './pages/AdminFeedback';
import PrivacyNotices from './pages/operator/PrivacyNotices';
import Announcements from './pages/operator/Announcements';
import NotificationSettings from './pages/operator/NotificationSettings'

// Member workspace
import MemberHome from './pages/member/MemberHome';
import MemberWorkspace from './pages/member/MemberWorkspace';
import MemberSurvey from './pages/member/MemberSurvey';
import MemberReport from './pages/member/MemberReport';
import MemberTasks from './pages/member/MemberTasks';

// Partner area
import PartnerWorkspaces from './pages/partner/PartnerWorkspaces';
import PartnerWorkspaceNew from './pages/partner/PartnerWorkspaceNew';
import PartnerWorkspace from './pages/partner/PartnerWorkspace';

import Profile from './pages/Profile';

/** The profile is shared, so it renders inside whichever shell the user belongs to. */
function SharedShell() {
  const { user } = useAuth();
  if (isPartner(user)) return <PartnerLayout />;
  return isOperator(user) ? <OperatorLayout /> : <MemberLayout />;
}

function AppRoutes() {
  const { user } = useAuth();
  const home = homePathFor(user);

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={user ? <Navigate to={home} replace /> : <Landing />} />
      <Route path="/login" element={user ? <Navigate to={home} replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to={home} replace /> : <Register />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/account/setup/:token" element={<AccountSetup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      {/* A public survey: the one page an unauthenticated person lands on on purpose. */}
      <Route path="/s/:token" element={<PublicSurvey />} />
      <Route path="/onboarding" element={<Onboarding />} />

      {/* Operator console */}
      <Route element={<ProtectedRoute area="operator" />}>
        <Route element={<OperatorLayout />}>
          <Route path="/app" element={<Navigate to="/app/workspaces" replace />} />
          <Route path="/app/workspaces" element={<WorkspacesList />} />
          <Route path="/app/workspaces/new" element={<WorkspaceNew />} />
          <Route path="/app/workspaces/:workspaceId" element={<WorkspaceDetail />} />
          <Route
            path="/app/workspaces/:workspaceId/surveys/:surveyId"
            element={<OperatorSurvey />}
          />
          <Route
            path="/app/workspaces/:workspaceId/reports/:artifactId"
            element={<OperatorReport />}
          />
          <Route path="/app/onboarding" element={<OnboardingList />} />
          <Route path="/app/onboarding/:sessionId" element={<OnboardingSession />} />
          <Route path="/app/agents" element={<AgentSettings />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute area="admin" />}>
        <Route element={<OperatorLayout />}>
          <Route path="/app/templates" element={<SurveyTemplates />} />
          <Route path="/app/users" element={<Admin />} />
          <Route path="/app/feedback" element={<AdminFeedback />} />
          <Route path="/app/notifications" element={<NotificationSettings />} />
          <Route path="/app/privacy" element={<PrivacyNotices />} />
          <Route path="/app/announcements" element={<Announcements />} />
        </Route>
      </Route>

      {/* The OAuth consent screen: operator-only, but outside the console shell —
          it is a decision reached from another application, not a page to browse. */}
      <Route element={<ProtectedRoute area="operator" />}>
        <Route path="/oauth/consent" element={<OAuthConsent />} />
      </Route>

      {/* Member workspace */}
      <Route element={<ProtectedRoute area="member" />}>
        <Route element={<MemberLayout />}>
          <Route path="/w" element={<MemberHome />} />
          <Route path="/w/:workspaceId" element={<MemberWorkspace />} />
          <Route path="/w/:workspaceId/surveys/:surveyId" element={<MemberSurvey />} />
          <Route path="/w/:workspaceId/reports/:artifactId" element={<MemberReport />} />
          <Route path="/w/:workspaceId/tasks" element={<MemberTasks />} />
        </Route>
      </Route>

      {/* Partner area: the companies a partner created, and their invitations */}
      <Route element={<ProtectedRoute area="partner" />}>
        <Route element={<PartnerLayout />}>
          <Route path="/partner" element={<PartnerWorkspaces />} />
          <Route path="/partner/new" element={<PartnerWorkspaceNew />} />
          <Route path="/partner/:workspaceId" element={<PartnerWorkspace />} />
        </Route>
      </Route>

      {/* Shared by every side */}
      <Route element={<ProtectedRoute />}>
        <Route element={<SharedShell />}>
          <Route path="/profile" element={<Profile />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={user ? home : '/'} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
